import React from 'react';
import {
  Outlet,
  redirect,
  useLocation,
  useMatches,
  useNavigate,
  useOutlet,
} from 'react-router-dom';
import {
  cleanPath,
  explicitRoutePathVariants,
  interpolateRoutePath,
  resolveRouteTo,
  samplePathForRoute,
} from 'utils/route_paths';
import RouteStoreRouterBridge from '../routes/route_store_router_bridge';
import useAuthReturnPath from '../routes/use_auth_return_path';

function metadataFromRouteDefinition(routeDefinition) {
  const {
    children,
    component,
    indexRedirects,
    name,
    path,
    redirects,
    ...metadata
  } = routeDefinition;
  return metadata;
}

function createRouteConfigNode(routeDefinition) {
  const {
    children = [],
    component,
    indexRedirects = [],
    name,
    path,
    redirects = [],
  } = routeDefinition;

  return {
    type: 'route',
    name: name || null,
    path: path || '',
    canonicalPath: path || '',
    component: component || null,
    handle: metadataFromRouteDefinition(routeDefinition),
    children: children.flatMap(child => createRouteConfigNodes(child)),
    indexRedirects: indexRedirects.map(indexRedirect => ({
      type: 'indexRedirect',
      to: indexRedirect.to,
    })),
    redirects: redirects.map(redirectConfig => ({
      type: 'redirect',
      from: redirectConfig.from,
      to: redirectConfig.to,
    })),
  };
}

function createRouteConfigNodes(routeDefinition) {
  const node = createRouteConfigNode(routeDefinition);
  const paths = explicitRoutePathVariants(node.path);

  if (paths.length <= 1) return [node];

  return paths.map(path => ({
    ...node,
    path,
    canonicalPath: node.path,
  }));
}

export function createRouteConfig(routeDefinitions) {
  return {
    routes: (routeDefinitions.routes || []).flatMap(route => createRouteConfigNodes(route)),
    redirects: (routeDefinitions.redirects || []).map(redirectConfig => ({
      type: 'redirect',
      from: redirectConfig.from,
      to: redirectConfig.to,
    })),
  };
}

export function flattenRouteConfig(routeConfig) {
  const records = [];
  const redirects = [...(routeConfig.redirects || [])];
  const namedRoutes = {};

  function addRecords(route, parentPath, parentChain) {
    const fullPath = route.path
      ? (route.path[0] === '/' ? cleanPath(route.path) : cleanPath(`${parentPath || ''}/${route.path}`))
      : parentPath || '';
    const canonicalFullPath = route.canonicalPath
      ? (route.canonicalPath[0] === '/' ? cleanPath(route.canonicalPath) : cleanPath(`${parentPath || ''}/${route.canonicalPath}`))
      : fullPath;
    const chain = [...parentChain, route];

    if (route.name && fullPath) {
      namedRoutes[route.name] = canonicalFullPath;
    }

    if (fullPath) {
      records.push({
        route,
        chain,
        path: fullPath,
        canonicalPath: canonicalFullPath,
      });
    }

    (route.redirects || []).forEach(redirectConfig => redirects.push(redirectConfig));
    (route.children || []).forEach(child => addRecords(child, fullPath, chain));
  }

  (routeConfig.routes || []).forEach(route => addRecords(route, '', []));

  return {
    records,
    redirects,
    namedRoutes,
  };
}

export function samplePathnamesForRouteConfig(routeConfig) {
  const registry = flattenRouteConfig(routeConfig);

  return registry.records
    .filter(record => record.path && record.path !== '*')
    .map(record => samplePathForRoute(record.path));
}

function routeHandle(route) {
  return {
    ...route.handle,
    name: route.name,
    path: route.path,
    canonicalPath: route.canonicalPath,
    legacyRoute: {
      ...route.handle,
      name: route.name,
      path: route.canonicalPath || route.path,
      component: route.component,
    },
  };
}

function RouteComponentElement({ route }) {
  const Component = route.component;

  return Component ? <Component /> : <Outlet />;
}

function RouteContextElement({ namedRoutes }) {
  const location = useLocation();
  const matches = useMatches();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const routes = React.useMemo(() => (
    matches
      .map(match => match.handle && match.handle.legacyRoute)
      .filter(Boolean)
  ), [matches]);
  const params = React.useMemo(() => (
    matches.reduce((result, match) => ({
      ...result,
      ...match.params,
    }), {})
  ), [matches]);
  const router = React.useMemo(() => ({
    location,
    routes,
    params,
    push(to) {
      navigate(resolveRouteTo(to, namedRoutes));
    },
    replace(to) {
      navigate(resolveRouteTo(to, namedRoutes), { replace: true });
    },
    createHref(to) {
      return resolveRouteTo(to, namedRoutes);
    },
  }), [location, namedRoutes, navigate, params, routes]);

  const routeName = routes[routes.length - 1] && routes[routes.length - 1].name;
  useAuthReturnPath(location, routeName);

  return (
    <RouteStoreRouterBridge
      router={router}
      routes={routes}
      params={params}
      routeName={routeName}
    >
      {outlet}
    </RouteStoreRouterBridge>
  );
}

function redirectRouteObject(redirectConfig, namedRoutes) {
  return explicitRoutePathVariants(redirectConfig.from).map(path => ({
    path,
    loader({ params }) {
      return redirect(interpolateRoutePath(resolveRouteTo(redirectConfig.to, namedRoutes), params));
    },
  }));
}

function indexRedirectRouteObject(path, indexRedirectConfig, namedRoutes) {
  return {
    path,
    loader({ params }) {
      return redirect(interpolateRoutePath(resolveRouteTo(indexRedirectConfig.to, namedRoutes), params));
    },
  };
}

function routeComponentObject(route) {
  return {
    element: <RouteComponentElement route={route} />,
    handle: routeHandle(route),
  };
}

function reactRouterRouteObject(record) {
  const [firstRoute, ...remainingRoutes] = record.chain;
  const routeObject = {
    ...routeComponentObject(firstRoute),
    path: record.path,
  };
  let parentRouteObject = routeObject;

  remainingRoutes.forEach((route, index) => {
    const childRouteObject = routeComponentObject(route);
    if (index === remainingRoutes.length - 1) {
      childRouteObject.index = true;
    }
    parentRouteObject.children = [childRouteObject];
    parentRouteObject = childRouteObject;
  });

  return routeObject;
}

export function createReactRouterRoutes(routeConfig) {
  const registry = flattenRouteConfig(routeConfig);
  const routes = registry.records
    .filter(record => !(record.route.indexRedirects || []).length)
    .map(record => reactRouterRouteObject(record));
  const indexRedirects = registry.records.flatMap(record => {
    return (record.route.indexRedirects || []).map(indexRedirectConfig => (
      indexRedirectRouteObject(record.path, indexRedirectConfig, registry.namedRoutes)
    ));
  });
  const redirects = registry.redirects.flatMap(redirectConfig => redirectRouteObject(redirectConfig, registry.namedRoutes));

  return [{
    element: <RouteContextElement namedRoutes={registry.namedRoutes} />,
    children: [
      ...routes,
      ...indexRedirects,
      ...redirects,
    ],
  }];
}
